import BackButton from "components/BackButton";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import AnalysisDashboards from "./components/AnalysisDashboards";
import EventLogSection from "./components/EventLogSection";
import GameBoardSection from "./components/GameBoardSection";
import HabitatSnapshotTimelineSection from "./components/HabitatSnapshotTimelineSection";
import PlayerDashboardsSection from "./components/PlayerDashboardsSection";
import PostGameAnalysisSection from "./components/PostGameAnalysisSection";
import StateTransitionSchemeSection from "./components/StateTransitionSchemeSection";
import {
  BUTTON_MUTED,
  BUTTON_PRIMARY,
  PAGE_BACKGROUND,
  PANEL_CLASS,
} from "./components/uiConstants";
import {
  clonePlayers,
  clonePlateau,
  getActLabel,
  type GameState,
  type HistoryEntry,
  type PlayerKey,
} from "./vowGameLogic";

type ExportedRosterEntry = {
  id: number;
  playerName?: string;
  role?: PlayerKey | string | null;
};

type VowExportPayload = {
  kind: "vow-game-export";
  version: number;
  exportedAt?: string;
  metadata?: {
    gameId?: string;
    hostId?: number | null;
    sessionVersion?: number;
  };
  roster?: ExportedRosterEntry[];
  state: GameState;
};

type RouteState = {
  payload?: VowExportPayload | null;
  fromGame?: boolean;
} | null;

const parseMetadata = (value: unknown): VowExportPayload["metadata"] => {
  if (!value || typeof value !== "object") return undefined;
  const data = value as Record<string, unknown>;
  const metadata: VowExportPayload["metadata"] = {};

  if (typeof data.gameId === "string") {
    metadata.gameId = data.gameId;
  }

  if (
    typeof data.hostId === "number" ||
    data.hostId === null
  ) {
    metadata.hostId = data.hostId as number | null;
  }

  if (typeof data.sessionVersion === "number") {
    metadata.sessionVersion = data.sessionVersion;
  }

  return Object.keys(metadata).length > 0 ? metadata : undefined;
};

const parseRoster = (value: unknown): ExportedRosterEntry[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const entries = value.reduce<ExportedRosterEntry[]>((acc, item) => {
    if (!item || typeof item !== "object") return acc;
    const record = item as Record<string, unknown>;
    if (typeof record.id !== "number") return acc;

    const entry: ExportedRosterEntry = {
      id: record.id,
      playerName: typeof record.playerName === "string" ? record.playerName : undefined,
      role:
        typeof record.role === "string"
          ? record.role
          : record.role === null
            ? null
            : undefined,
    };
    acc.push(entry);
    return acc;
  }, []);
  return entries.length > 0 ? entries : undefined;
};

const normalizePayload = (value: unknown): VowExportPayload => {
  if (!value || typeof value !== "object") {
    throw new Error("Export file is empty or invalid.");
  }

  const data = value as Record<string, unknown>;

  if (data.kind === "vow-game-export" && data.state) {
    const version =
      typeof data.version === "number"
        ? data.version
        : Number.parseInt(String(data.version ?? "1"), 10) || 1;

    return {
      kind: "vow-game-export",
      version,
      exportedAt: typeof data.exportedAt === "string" ? data.exportedAt : undefined,
      metadata: parseMetadata(data.metadata),
      roster: parseRoster(data.roster),
      state: data.state as GameState,
    };
  }

  if ("annee" in data && "players" in data && "history" in data) {
    return {
      kind: "vow-game-export",
      version: 0,
      state: data as unknown as GameState,
    };
  }

  throw new Error("Unrecognized VOW game export format.");
};

const formatTimestamp = (value?: string) => {
  if (!value) return undefined;
  const timestamp = new Date(value);
  if (Number.isNaN(timestamp.getTime())) {
    return value;
  }
  return timestamp.toLocaleString();
};

const VowAnalysis: React.FC = () => {
  const [payload, setPayload] = useState<VowExportPayload | null>(null);
  const [selectedHistoryIndex, setSelectedHistoryIndex] = useState<number>(-1);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const hasLoadedRoutePayloadRef = useRef(false);
  const location = useLocation();
  const routeState = (location.state as RouteState) ?? null;
  const routePayload = routeState?.payload ?? null;

  const historyEntries = payload?.state.history ?? [];
  const currentHistory: HistoryEntry | undefined =
    selectedHistoryIndex >= 0 ? historyEntries[selectedHistoryIndex] : undefined;
  const previousHistory: HistoryEntry | undefined =
    selectedHistoryIndex > 0 ? historyEntries[selectedHistoryIndex - 1] : undefined;

  const snapshotPlateau = useMemo(() => {
    if (!payload) return null;
    const snapshots = payload.state.boardSnapshots ?? [];
    if (snapshots.length === 0) {
      return payload.state.plateau;
    }

    if (selectedHistoryIndex >= 0) {
      const clampedIndex = Math.min(
        selectedHistoryIndex + 1,
        snapshots.length - 1
      );
      return snapshots[clampedIndex]?.plateau ?? payload.state.plateau;
    }

    return snapshots[snapshots.length - 1]?.plateau ?? payload.state.plateau;
  }, [payload, selectedHistoryIndex]);

  const displayedState = useMemo<GameState | null>(() => {
    if (!payload) return null;
    const plateau = clonePlateau(snapshotPlateau ?? payload.state.plateau);

    if (!currentHistory) {
      return {
        ...payload.state,
        plateau,
      };
    }

    return {
      ...payload.state,
      annee: currentHistory.annee,
      scores: { ...currentHistory.scores },
      players: clonePlayers(currentHistory.players),
      wmChoice: currentHistory.wmChoice,
      plateau,
    };
  }, [currentHistory, payload, snapshotPlateau]);

  const exportedAtLabel = useMemo(() => formatTimestamp(payload?.exportedAt), [payload]);

  const resetSelection = useCallback((state: VowExportPayload | null) => {
    if (!state) {
      setSelectedHistoryIndex(-1);
      return;
    }
    setSelectedHistoryIndex(
      state.state.history.length > 0 ? state.state.history.length - 1 : -1
    );
  }, []);

  useEffect(() => {
    if (!routePayload) return;
    if (hasLoadedRoutePayloadRef.current) return;
    setPayload(routePayload);
    resetSelection(routePayload);
    setErrorMessage(null);
    hasLoadedRoutePayloadRef.current = true;
  }, [resetSelection, routePayload]);

  const handleFileSelection = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      try {
        const rawText = await file.text();
        const parsed = normalizePayload(JSON.parse(rawText));
        setPayload(parsed);
        resetSelection(parsed);
        setErrorMessage(null);
      } catch (error) {
        console.error(error);
        setPayload(null);
        resetSelection(null);
        setErrorMessage(
          "Unable to read that file. Please choose a JSON export created from the VOW game host view."
        );
      } finally {
        // Allow the same file to be re-selected if needed.
        event.target.value = "";
      }
    },
    [resetSelection]
  );

  const handleClearData = useCallback(() => {
    setPayload(null);
    resetSelection(null);
    setErrorMessage(null);
  }, [resetSelection]);

  const handleDownloadCurrentData = useCallback(() => {
    if (!payload) return;
    const downloadName = `vow-game-${payload.metadata?.gameId || "session"}-${payload.metadata?.sessionVersion ?? "data"}.json`;
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: "application/json",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = downloadName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  }, [payload]);

  const goToPreviousHistory = useCallback(() => {
    setSelectedHistoryIndex((current) => Math.max(0, current - 1));
  }, []);

  const goToNextHistory = useCallback(() => {
    setSelectedHistoryIndex((current) => {
      if (!payload) return current;
      const lastIndex = payload.state.history.length - 1;
      if (lastIndex < 0) return current;
      return Math.min(lastIndex, current + 1);
    });
  }, [payload]);

  const selectedActLabel = currentHistory ? getActLabel(currentHistory.annee) : undefined;
  const historyCount = historyEntries.length;
  const canStepBackward = selectedHistoryIndex > 0;
  const canStepForward =
    payload !== null && selectedHistoryIndex >= 0 && selectedHistoryIndex < historyCount - 1;
  const showDownloadFromGame = Boolean(payload && (routeState?.fromGame ?? false));

  return (
    <div className={`flex overflow-auto ${PAGE_BACKGROUND} h-[100vh] w-full`}>
      <div className="mx-auto w-full max-w-6xl px-4 py-8">
        <header className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">VOW Game Analysis</h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-700">
              Upload a JSON export from the host dashboard to review post-game dashboards,
              replay scoring by act, and explore the session log. If you arrived from the host
              session, your latest game data appears automatically.
            </p>
          </div>
          <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center">
            <BackButton useAbsolute={false} />
            <Link
              to="/vow/play"
              className={`${BUTTON_MUTED} inline-block`}
            >
              Return to VOW Hub
            </Link>
            {showDownloadFromGame && (
              <button
                type="button"
                onClick={handleDownloadCurrentData}
                className={`${BUTTON_PRIMARY} inline-block`}
              >
                Download Game Data
              </button>
            )}
          </div>
        </header>

        <section className={`${PANEL_CLASS} mt-6 p-6`}>
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Upload Export</h2>
              <p className="mt-1 text-sm text-slate-700">
                Choose the JSON file downloaded from the host admin panel after ending a game.
                Automatic loading is available when navigating here directly from the host view.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <label
                htmlFor="vow-export-upload"
                className={`${BUTTON_PRIMARY} cursor-pointer`}
              >
                Select export file
              </label>
              <input
                id="vow-export-upload"
                type="file"
                accept="application/json"
                className="hidden"
                onChange={handleFileSelection}
              />
              {payload && (
                <button
                  type="button"
                  onClick={handleClearData}
                  className={`${BUTTON_MUTED}`}
                >
                  Clear data
                </button>
              )}
            </div>
          </div>
          {errorMessage && (
            <p className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {errorMessage}
            </p>
          )}
        </section>

        {payload && displayedState && (
          <>
            <section className={`${PANEL_CLASS} mt-6 p-6`}>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <h3 className="text-sm font-semibold uppercase text-slate-700">Game Code</h3>
                  <p className="text-base font-medium text-slate-900">
                    {payload.metadata?.gameId ?? "Unknown"}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold uppercase text-slate-700">
                    Exported
                  </h3>
                  <p className="text-base font-medium text-slate-900">
                    {exportedAtLabel ?? "Timestamp unavailable"}
                  </p>
                </div>
                <div>
                  <h3 className="text-sm font-semibold uppercase text-slate-700">
                    Acts Recorded
                  </h3>
                  <p className="text-base font-medium text-slate-900">
                    {historyCount > 0 ? historyCount : "No simulation data"}
                  </p>
                </div>
                {payload.metadata?.hostId !== undefined && (
                  <div>
                    <h3 className="text-sm font-semibold uppercase text-slate-700">
                      Host ID
                    </h3>
                    <p className="text-base font-medium text-slate-900">
                      {payload.metadata?.hostId ?? "Unknown"}
                    </p>
                  </div>
                )}
                {payload.metadata?.sessionVersion !== undefined && (
                  <div>
                    <h3 className="text-sm font-semibold uppercase text-slate-700">
                      Session Version
                    </h3>
                    <p className="text-base font-medium text-slate-900">
                      {payload.metadata?.sessionVersion}
                    </p>
                  </div>
                )}
                {payload.roster && payload.roster.length > 0 && (
                  <div className="sm:col-span-2 lg:col-span-3">
                    <h3 className="text-sm font-semibold uppercase text-slate-700">
                      Roster Summary
                    </h3>
                    <ul className="mt-2 space-y-1 text-sm text-slate-800">
                      {payload.roster.map((entry) => (
                        <li key={entry.id}>
                          <span className="font-semibold text-slate-900">
                            {entry.playerName || `Player ${entry.id}`}
                          </span>
                          {entry.role && (
                            <span className="text-slate-600"> — {String(entry.role)}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              {showDownloadFromGame && (
                <p className="mt-4 rounded-md border border-slate-300 bg-[#f4f0e0] px-3 py-2 text-sm text-slate-800">
                  Session data loaded directly from the recently ended host game.
                </p>
              )}
            </section>

            {historyCount > 0 && (
              <section className={`${PANEL_CLASS} mt-6 p-6`}>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h2 className="text-xl font-semibold text-slate-900">Act Explorer</h2>
                    <p className="text-sm text-slate-700">
                      Use the controls to review how scores and decisions evolved each act.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={goToPreviousHistory}
                      disabled={!canStepBackward}
                      className={`px-3 py-2 text-sm font-semibold ${
                        canStepBackward
                          ? BUTTON_MUTED
                          : "border-2 border-black bg-[#d1c7b0] text-slate-700 cursor-not-allowed"
                      }`}
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      onClick={goToNextHistory}
                      disabled={!canStepForward}
                      className={`px-3 py-2 text-sm font-semibold ${
                        canStepForward
                          ? BUTTON_MUTED
                          : "border-2 border-black bg-[#d1c7b0] text-slate-700 cursor-not-allowed"
                      }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
                <div className="mt-4 flex flex-col gap-3">
                  <input
                    type="range"
                    min={0}
                    max={historyCount - 1}
                    step={1}
                    value={selectedHistoryIndex}
                    onChange={(event) => setSelectedHistoryIndex(Number(event.target.value))}
                    className="w-full"
                  />
                  <p className="text-sm text-slate-700">
                    Viewing:{" "}
                    <span className="font-semibold text-slate-900">
                      {selectedActLabel ?? "Final state"}
                    </span>{" "}
                    ({selectedHistoryIndex + 1} of {historyCount})
                  </p>
                </div>
              </section>
            )}

            <div className="mt-6 grid gap-4 [grid-auto-flow:dense] grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 w-full">
              <div className="col-span-6 flex w-full flex-col gap-6">
                <GameBoardSection
                  gameState={displayedState}
                  canPerformActions={false}
                />

                <PlayerDashboardsSection
                  gameState={displayedState}
                  lastHistory={currentHistory ?? undefined}
                  previousHistory={previousHistory}
                />

                <PostGameAnalysisSection gameState={displayedState} />
                <HabitatSnapshotTimelineSection snapshots={payload.state.boardSnapshots || []} />
                <StateTransitionSchemeSection history={payload.state.history} />
                <AnalysisDashboards gameState={payload.state} />
                <EventLogSection entries={payload.state.log} />
              </div>

            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VowAnalysis;
